import { Device } from '@twilio/voice-sdk';
import { useEffect } from 'react';
import { TwilioCall } from '@/types/app.types';
import { TWILIO_CALL_EVENTS } from '@shared/constants';
import { useDeviceStore } from '@modules/phone/store/device.store';
import { useWaitingCallStore } from '@modules/phone/store/waiting-call.store';
import { useWaitingCallBeep } from '@modules/phone/features/incoming/hooks/use-waiting-call-beep';
import { usePhoneStore } from '@modules/phone/store/phone.store';
import { phoneModes } from '@modules/phone/constants/phone.constants';

export function useWaitingCallEvents() {
  const { waitingCall, resetWaitingCall } = useWaitingCallStore();

  // notification tone lives here rather than in WaitingCallBar so it does not depend on the
  // phone panel being mounted in any particular mode
  useWaitingCallBeep();
  useCollapseOngoingCallWhileWaiting(waitingCall);

  function onWaitingCallEnded() {
    const device = useDeviceStore.getState().device?.providerDevice as Device | undefined;
    device?.audio?.incoming(true);
    resetWaitingCall();
  }

  function handleWaitingCallEvents() {
    waitingCall?.on(TWILIO_CALL_EVENTS.cancel, onWaitingCallEnded);
    waitingCall?.on(TWILIO_CALL_EVENTS.disconnect, onWaitingCallEnded);
    waitingCall?.on(TWILIO_CALL_EVENTS.reject, onWaitingCallEnded);

    return handleUnregisterWaitingCallEvents;
  }

  function handleUnregisterWaitingCallEvents() {
    waitingCall?.off(TWILIO_CALL_EVENTS.cancel, onWaitingCallEnded);
    waitingCall?.off(TWILIO_CALL_EVENTS.disconnect, onWaitingCallEnded);
    waitingCall?.off(TWILIO_CALL_EVENTS.reject, onWaitingCallEnded);
  }

  useEffect(handleWaitingCallEvents, [waitingCall]);
}

/**@description The waiting-call bar is stacked above the phone panel, and the two at full size need
 * more vertical room than a 13" laptop viewport has. Collapsing the ongoing call for as long as the
 * second call rings keeps the whole column on screen; the previous state is restored afterwards so
 * accepting or declining returns the panel to how the user left it. Only the in-call views have a
 * collapsed layout, so dialer mode is left alone. */
function useCollapseOngoingCallWhileWaiting(waitingCall: TwilioCall | null) {
  useEffect(
    function collapseOngoingCall() {
      if (!waitingCall) return;

      const { mode, isMinimized, minimizePhone, maximizePhone } = usePhoneStore.getState();
      if (mode !== phoneModes.inCall && mode !== phoneModes.transferCall) return;
      if (isMinimized) return;

      minimizePhone();

      return maximizePhone;
    },
    [waitingCall]
  );
}
